import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { trackChatOpened, trackChatLeadCaptured } from "../lib/analytics";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { MessageCircle, X, Send, Loader2, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function SalesChatBot() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [captured, setCaptured] = useState(null);
  const [context, setContext] = useState({});
  const [booked, setBooked] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "assistant", text: "Hey there! I'm the Stansbury AI assistant. I help businesses find where AI can drive real revenue growth. What kind of business are you running?" }]);
    }
  }, [open, messages.length]);

  const sendMessage = async () => {
    if (!input.trim() || sending) return;
    const userMsg = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", text: userMsg }]);
    setSending(true);
    try {
      const { data } = await api.post("/chat/message", { message: userMsg, session_id: sessionId, context });
      setSessionId(data.session_id);
      setMessages(prev => [...prev, { role: "assistant", text: data.reply }]);
      if (data.captured) {
        setCaptured(data.captured);
        setContext(prev => ({ ...prev, ...data.captured }));
        trackChatLeadCaptured(data.captured.email);
        // Auto-create lead
        try { await api.post("/chat/book", data.captured); } catch (e) { console.error("Lead capture error:", e); }
      }
    } catch (err) {
      setMessages(prev => [...prev, { role: "assistant", text: "Sorry, I had a connection hiccup. Could you try that again?" }]);
    } finally {
      setSending(false);
    }
  };

  const handleBookCall = () => {
    const params = new URLSearchParams();
    if (captured?.name) params.set("name", captured.name);
    if (captured?.email) params.set("email", captured.email);
    if (captured?.website) params.set("website", captured.website);
    navigate(`/booking?${params}`);
    setOpen(false);
  };

  return (
    <>
      {/* Toggle Button */}
      <AnimatePresence>
        {!open && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            onClick={() => { setOpen(true); trackChatOpened(); }}
            className="fixed bottom-6 right-6 z-[9997] w-14 h-14 bg-[#0033CC] text-white flex items-center justify-center shadow-lg hover:bg-[#002299] transition-colors"
            data-testid="chat-toggle"
          >
            <MessageCircle className="w-6 h-6" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-6 right-6 z-[9997] w-[360px] max-w-[calc(100vw-2rem)] bg-white border border-slate-200 shadow-xl flex flex-col"
            style={{ height: "min(520px, calc(100vh - 6rem))" }}
            data-testid="chat-window"
          >
            {/* Header */}
            <div className="bg-[#0F172A] px-4 py-3 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                <div>
                  <p className="text-sm font-['Outfit'] font-medium text-white">Stansbury AI</p>
                  <p className="text-[10px] font-['IBM_Plex_Sans'] text-slate-400">Growth Assistant</p>
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-white transition-colors" data-testid="chat-close">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
              {messages.map((msg, i) => (
                <div key={`idx-${i}`} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] px-3 py-2 text-sm font-['IBM_Plex_Sans'] leading-relaxed ${
                    msg.role === "user"
                      ? "bg-[#0033CC] text-white"
                      : "bg-slate-100 text-[#0F172A]"
                  }`}>
                    {msg.text}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className="bg-slate-100 px-3 py-2">
                    <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                  </div>
                </div>
              )}
              {/* Book Call CTA */}
              {captured && !booked && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="pt-2">
                  <div className="bg-[#E5EDFF] border border-[#0033CC]/20 p-3">
                    <p className="text-xs font-['Outfit'] font-medium text-[#0033CC] mb-2">Ready to take the next step?</p>
                    <Button onClick={handleBookCall} className="w-full bg-[#0033CC] text-white hover:bg-[#002299] rounded-none h-9 text-sm font-['Outfit']" data-testid="chat-book-call">
                      Book Your Strategy Call <ArrowRight className="w-3.5 h-3.5 ml-1" />
                    </Button>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Input */}
            <div className="border-t border-slate-200 p-3 flex gap-2 flex-shrink-0">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                placeholder="Type a message..."
                className="flex-1 rounded-none border-slate-300 h-9 text-sm"
                data-testid="chat-input"
              />
              <Button onClick={sendMessage} disabled={!input.trim() || sending} className="bg-[#0033CC] text-white hover:bg-[#002299] rounded-none h-9 w-9 p-0" data-testid="chat-send">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
